/**
 * Leaving this app for a same-origin URL — either the `/v2` app taking over a
 * route, or `/v2` handing one back — without booting a stale build.
 *
 * The two apps share one origin, and a hop between them is `window.location.
 * assign(path)`: a plain navigation. A plain navigation honours the browser's
 * HTTP cache, and the thing it fetches is the other app's entry document,
 * `index.html`. Each app now serves that document with `Cache-Control:
 * no-cache, no-store, must-revalidate`, but that only stops a *future*
 * response from being cached — it does nothing about a copy the browser
 * already has sitting in its cache from an earlier visit, before the header
 * existed, or from any response the header failed to reach.
 *
 * Worse, a cached entry document without an explicit freshness header is not
 * treated as stale just because it is old: browsers fall back to heuristic
 * freshness, roughly 10% of the time since the response's `Last-Modified`.
 * An `index.html` last deployed five days ago reads as fresh for about twelve
 * hours after being fetched, so a hop lands on it straight from the cache —
 * no network request, no chance to notice a new build exists — and boots a
 * bundle that can be days old. A manual reload works around it by coincidence
 * (a reload revalidates), which is why the symptom looks like "stale after a
 * hop, fine after refresh".
 *
 * The fix is to force one network round trip before handing control to
 * `location.assign`: `fetch(url, { cache: "reload" })` always goes to the
 * network and updates (or evicts) whatever the cache held for that URL, so
 * the navigation that follows can no longer be served the old copy. That
 * fetch is a best-effort refresh, not a gate — if it fails or a flaky network
 * takes too long, the user still has to get to the other app, just with the
 * (already-accepted) risk of a stale build rather than being stuck.
 *
 * That round trip is a window, up to `timeoutMs` long, in which the browser
 * can move under the decision — a Back press is the easy case. Assigning at
 * the end of it anyway would undo the move the person just made. So the hop
 * is confirmed once more before it happens: if the address changed while the
 * document was being fetched, nothing is assigned, and the caller is told the
 * browser did not leave so that it can judge the new address instead.
 */
export type LeaveDeps = {
  fetch: (
    url: string,
    init: {
      cache: "reload";
      credentials: "same-origin";
      headers: { Accept: string };
    },
  ) => Promise<unknown>;
  assign: (url: string) => void;
  /** Where the browser is right now; compared before and after the fetch. */
  readHref: () => string;
  timeoutMs: number;
};

/** Whether the browser was actually sent on its way. */
export type LeaveResult = { left: boolean };

export function leaveTo(
  url: string,
  deps?: Partial<LeaveDeps>,
): Promise<LeaveResult> {
  const fetchFn = deps?.fetch ?? ((u, init) => window.fetch(u, init));
  const assignFn = deps?.assign ?? ((u) => window.location.assign(u));
  const readHref = deps?.readHref ?? (() => window.location.href);
  const timeoutMs = deps?.timeoutMs ?? 1500;
  const decidedAt = readHref();

  return new Promise<LeaveResult>((resolve) => {
    let settled = false;

    const leave = () => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timer);

      if (readHref() !== decidedAt) {
        resolve({ left: false });
        return;
      }

      assignFn(url);
      resolve({ left: true });
    };

    const timer = setTimeout(leave, timeoutMs);

    fetchFn(url, {
      cache: "reload",
      credentials: "same-origin",
      headers: { Accept: "text/html" },
    }).then(leave, leave);
  });
}
